import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, List, Card, Typography, Button, Space } from 'antd';
import { BellOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const NotificationSystem = ({ currentUser }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (currentUser?.id) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchNotifications = async () => {
    try {
      // For now, we'll simulate notifications by checking for new tasks
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/user/${currentUser.id}`);
      if (response.ok) {
        const tasks = await response.json();
        
        // Create notifications for recently assigned tasks (last 24 hours)
        const recentTasks = tasks.filter(task => {
          const createdAt = dayjs(task.createdAt);
          return createdAt.isAfter(dayjs().subtract(24, 'hours'));
        });

        const taskNotifications = recentTasks.map(task => ({
          id: `task-${task._id}`,
          type: 'task_assigned',
          title: 'New Task Assigned',
          message: `You have been assigned to task: ${task.title}`,
          data: task,
          createdAt: task.createdAt,
          read: false
        }));

        setNotifications(taskNotifications);
        setUnreadCount(taskNotifications.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => {
      const notification = notifications.find(n => n.id === notificationId);
      return notification && !notification.read ? Math.max(0, prev - 1) : prev;
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_assigned':
        return '📋';
      case 'task_completed':
        return '✅';
      case 'task_overdue':
        return '⏰';
      default:
        return '📢';
    }
  };

  const notificationMenu = (
    <Card style={{ width: 350, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>Notifications</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        )}
      </div>
      
      {notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
          No notifications
        </div>
      ) : (
        <List
          dataSource={Array.isArray(notifications) ? notifications : []}
          renderItem={(notification) => (
            <List.Item
              style={{
                backgroundColor: notification.read ? 'transparent' : '#f0f8ff',
                padding: '8px 0',
                borderRadius: 4,
                marginBottom: 4
              }}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => markAsRead(notification.id)}
                  disabled={notification.read}
                />,
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => deleteNotification(notification.id)}
                />
              ]}
            >
              <List.Item.Meta
                avatar={<span style={{ fontSize: 16 }}>{getNotificationIcon(notification.type)}</span>}
                title={
                  <div style={{ fontSize: 14 }}>
                    <Text strong={!notification.read}>{notification.title}</Text>
                    {!notification.read && <Badge dot style={{ marginLeft: 8 }} />}
                  </div>
                }
                description={
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      {notification.message}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(notification.createdAt).fromNow()}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  return (
    <Dropdown
      popupRender={() => notificationMenu}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small">
        <Button
          type="text"
          icon={<BellOutlined />}
          style={{ fontSize: 16 }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationSystem;
